import type {ReactNode} from 'react';

import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';

import {P} from 'UI/components/layout/Text';

import type {Theme} from '@emotion/react';

/** The available visual variants for the {@link Badge} component. */
export type BadgeVariant = 'category' | 'metric' | 'rating';

interface VariantStyledProps {
    /** The badge variant for styling. */
    $variant: BadgeVariant;
}

/**
 * The props for the `Badge` component.
 * Configures a small pill-shaped label for categories, metrics, or ratings.
 */
interface ComponentProps {
    /** An optional icon element rendered before the label. */
    icon?: ReactNode;
    /** The text label displayed inside the badge. */
    label: string;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'Badge'
     */
    testId?: string;
    /**
     * The visual variant of the badge.
     * - `'category'` — primaryPale bg, dark green text (tour categories).
     * - `'metric'` — surfHigh bg, inkSecondary text (distance, duration).
     * - `'rating'` — accentPale bg, accentDefault text (star ratings).
     *
     * @default 'metric'
     */
    variant?: BadgeVariant;
}

/**
 * A small pill-shaped badge for displaying categories, metrics, or ratings.
 * Matches the Figma Badge/Tag component at H=28, border-radius 9999.
 *
 * @param props         The component props.
 * @param props.icon    An optional icon element rendered before the label.
 * @param props.label   The text label to display.
 * @param props.testId  An optional test ID used for automation selectors. Defaults to `'Badge'`.
 * @param props.variant The visual variant. Defaults to `'metric'`.
 * @returns A JSX element rendering the Badge.
 *
 * @example
 * ```tsx
 * <Badge label="Historisch" variant="category" />
 * <Badge label="2.4 km" variant="metric" />
 * <Badge icon={<StarFilledIcon />} label="4.8" variant="rating" />
 * ```
 */
const Badge = ({icon, label, testId = 'Badge', variant = 'metric'}: ComponentProps) => (
    <Wrapper $variant={variant} data-cy={testId}>
        {/* ⚠️ Figma: 11px SemiBold — using labelSmall (11px/500) with font-weight override to 600 */}
        {icon && <BadgeIcon>{icon}</BadgeIcon>}
        <BadgeLabel $size="labelSmall" $variant={variant} as="span">{label}</BadgeLabel>
    </Wrapper>
);

Badge.displayName = 'Badge';

/**
 * Returns the background color for the given badge variant.
 *
 * @param props          The styled component props.
 * @param props.$variant The badge variant.
 * @param props.theme    The Emotion theme.
 * @returns The CSS background-color value.
 */
const getBackground = ({$variant, theme}: VariantStyledProps & {theme: Theme}) => {
    switch ($variant) {
        case 'category':
            return theme.colors.primaryPale;
        case 'rating':
            return theme.colors.accentPale;
        case 'metric':
        default:
            return theme.colors.surfaceHigh;
    }
};

/**
 * Returns the text color for the given badge variant.
 *
 * @param props          The styled component props.
 * @param props.$variant The badge variant.
 * @param props.theme    The Emotion theme.
 * @returns The CSS color value.
 */
const getColor = ({$variant, theme}: VariantStyledProps & {theme: Theme}) => {
    switch ($variant) {
        case 'category':
            return theme.colors.primaryDefault;
        case 'rating':
            return theme.colors.accentDefault;
        case 'metric':
        default:
            return theme.colors.inkSecondary;
    }
};

export {Badge};

// ── Styled ──────────────────────────────────────────────────

const Wrapper = styled.span<VariantStyledProps>`
    align-items: center;
    background: ${getBackground};
    border-radius: 9999px;
    display: inline-flex;
    gap: ${spacing(1)};
    height: 2.8rem;
    justify-content: center;
    padding: 0 ${spacing(3)};
`;

const BadgeIcon = styled.span`
    align-items: center;
    display: inline-flex;
    font-size: 1.2rem;
    line-height: 1;

    & > svg {
        height: 1em;
        width: 1em;
    }
`;

const BadgeLabel = styled(P)<VariantStyledProps>`
    color: ${getColor};
    font-weight: 600;
`;